import downloadS3File from '@/utils/downloadS3File';

import { getType } from 'typesafe-actions';

import { defaultCapStrikeForm } from '../PricingSummaryWidget/components/CapStrikeForm/constants';
import { defaultHedgeSimulationForm } from '../PricingSummaryWidget/components/HedgeSimulationForm/constants';
import { defaultValuesPricingSummaryForm } from '../PricingSummaryWidget/components/PricingSummaryForm/constants';
import { defaultDebtScheduleDataForm } from '../PricingSummaryWidget/components/SimulatedDataForm/components/DebtScheduleDrawer/constants';
import { defaultValuesSimulatedDataForm } from '../PricingSummaryWidget/components/SimulatedDataForm/constants';
import {
  defaultValuesCashflowThroughTime,
  defaultValuesDebtHedgeSchedule,
  defaultValuesIRMarketEnvironment,
} from '../constants';
import { WidgetKeys } from '../enums';
import { generateValidationKey } from '../utils';

import * as irAnalysisActions from './actions';
import { IrAnalysisActionType, IrAnalysisState } from './types';

const initialIrAnalysisState: IrAnalysisState = {
  irIndices: { data: [], loading: false, error: null },
  swapTenors: { data: [], loading: false, error: null },
  validations: {},
  irMarketEnvironment: defaultValuesIRMarketEnvironment,
  pricingSummary: {
    ...defaultValuesPricingSummaryForm,
    ...defaultHedgeSimulationForm,
    ...defaultCapStrikeForm,
    capMode: Boolean(defaultCapStrikeForm.capMode),
    ...defaultValuesSimulatedDataForm,
    debtType: defaultDebtScheduleDataForm.debtType,
    debtSchedule: [],
    debtScheduleOverridden: false,
  },
  debtHedgeSchedule: {
    ...defaultValuesDebtHedgeSchedule,
    debtTableData: [],
    debtTableLoading: false,
    tradeTableData: [],
    tradeTableLoading: false,
    validations: {
      trades: {
        loading: false,
        valid: null,
        error: null,
        tradeCompanyEntityId: null,
        debtCompanyEntityId: null,
      },
      debts: {
        loading: false,
        valid: null,
        error: null,
        tradeCompanyEntityId: null,
        debtCompanyEntityId: null,
      },
    },
  },
  cashflowThroughTime: {
    ...defaultValuesCashflowThroughTime,
    debtTableData: [],
    debtTableLoading: false,
    tradeTableData: [],
    tradeTableLoading: false,
    validations: {
      trades: {
        loading: false,
        valid: null,
        error: null,
        tradeCompanyEntityId: null,
        debtCompanyEntityId: null,
      },
      debts: {
        loading: false,
        valid: null,
        error: null,
        tradeCompanyEntityId: null,
        debtCompanyEntityId: null,
      },
    },
  },
  downloadRMToolsReport: { loading: false, error: null },
  closestValuationDate: { loading: false, value: '', error: null },
  userWidgets: { data: [], loading: false, error: null },

  disableDebtAndHedge: false,
  disableCFTT: false,
  swapRateChart: { data: [], loading: false },
};

const irAnalysisReducer = (
  state: IrAnalysisState = initialIrAnalysisState,
  action: IrAnalysisActionType,
): IrAnalysisState => {
  switch (action.type) {
    case getType(irAnalysisActions.fetchIRIndicesActions.request):
      return {
        ...state,
        irIndices: { ...state.irIndices, loading: true, error: null },
      };
    case getType(irAnalysisActions.fetchIRIndicesActions.success):
      return {
        ...state,
        irIndices: {
          ...state.irIndices,
          loading: false,
          data: action.payload.data,
          error: null,
        },
      };
    case getType(irAnalysisActions.fetchIRIndicesActions.failure):
      return {
        ...state,
        irIndices: {
          ...state.irIndices,
          loading: false,
          error: action.payload.error,
        },
      };
    case getType(irAnalysisActions.fetchSwapTenorsActions.request):
      return {
        ...state,
        swapTenors: { ...state.swapTenors, loading: true, error: null },
      };
    case getType(irAnalysisActions.fetchSwapTenorsActions.success):
      return {
        ...state,
        swapTenors: {
          ...state.swapTenors,
          loading: false,
          data: action.payload.data,
          error: null,
        },
      };
    case getType(irAnalysisActions.fetchSwapTenorsActions.failure):
      return {
        ...state,
        swapTenors: {
          ...state.swapTenors,
          loading: false,
          error: action.payload.error,
        },
      };
    case getType(irAnalysisActions.updatePricingSummaryState):
      return {
        ...state,
        pricingSummary: { ...state.pricingSummary, ...action.payload },
      };

    case getType(irAnalysisActions.updateDebtHedgeScheduleActions.success):
      return {
        ...state,
        debtHedgeSchedule: { ...state.debtHedgeSchedule, ...action.payload },
      };

    case getType(irAnalysisActions.updateCashflowThroughTimeActions.success):
      return {
        ...state,
        cashflowThroughTime: {
          ...state.cashflowThroughTime,
          ...action.payload,
        },
      };

    case getType(irAnalysisActions.updateIRMarketEnvironmentActions.success):
      return {
        ...state,
        irMarketEnvironment: {
          ...state.irMarketEnvironment,
          ...action.payload,
        },
      };
    case getType(irAnalysisActions.downloadRMToolsReportActions.request):
      return {
        ...state,
        downloadRMToolsReport: { loading: true, error: null },
      };
    case getType(irAnalysisActions.downloadRMToolsReportActions.success): {
      const payload = action.payload;
      if (payload.success && payload.data && !Array.isArray(payload.data)) {
        const { file_s3_url, file_name } = payload.data;
        if (file_s3_url && file_name) downloadS3File(file_s3_url, file_name);
      }
      return {
        ...state,
        downloadRMToolsReport: { loading: false, error: null },
      };
    }
    case getType(irAnalysisActions.downloadRMToolsReportActions.failure):
      return {
        ...state,
        downloadRMToolsReport: { loading: false, error: action.payload.error },
      };

    case getType(irAnalysisActions.fetchTradesActions.request): {
      const { widgetType } = action.payload;
      const key = widgetType;
      return { ...state, [key]: { ...state[key], tradeTableLoading: true } };
    }
    case getType(irAnalysisActions.fetchTradesActions.success): {
      const { trades, widgetType } = action.payload;
      const key = widgetType;
      return {
        ...state,
        [key]: {
          ...state[key],
          tradeTableData: trades,
          tradeTableLoading: false,
        },
      };
    }
    case getType(irAnalysisActions.fetchTradesActions.failure): {
      const key = action.payload.widgetType;
      return { ...state, [key]: { ...state[key], tradeTableLoading: false } };
    }
    case getType(irAnalysisActions.fetchDebtsActions.request): {
      const key = action.payload.widgetType;
      return { ...state, [key]: { ...state[key], debtTableLoading: true } };
    }
    case getType(irAnalysisActions.fetchDebtsActions.success): {
      const key = action.payload.widgetType;
      return {
        ...state,
        [key]: {
          ...state[key],
          debtTableData: action.payload.debts,
          debtTableLoading: false,
        },
      };
    }
    case getType(irAnalysisActions.fetchDebtsActions.failure): {
      const key = action.payload.widgetType;
      return { ...state, [key]: { ...state[key], debtTableLoading: false } };
    }

    case getType(irAnalysisActions.getClosestTradeValuationDateActions.request):
      return {
        ...state,
        closestValuationDate: {
          ...state.closestValuationDate,
          loading: true,
          error: null,
        },
      };
    case getType(irAnalysisActions.getClosestTradeValuationDateActions.success):
      return {
        ...state,
        closestValuationDate: {
          loading: false,
          value: action.payload.value_date,
          error: null,
        },
      };
    case getType(irAnalysisActions.getClosestTradeValuationDateActions.failure):
      return {
        ...state,
        closestValuationDate: {
          loading: false,
          value: '',
          error: action.payload.error,
        },
      };

    case getType(irAnalysisActions.validateIrIndexActions.request): {
      const { widget, date } = action.payload;
      const key = generateValidationKey(widget, date);
      return {
        ...state,
        validations: {
          ...state.validations,
          [key]: { loading: true, valid: null, error: null },
        },
      };
    }
    case getType(irAnalysisActions.validateIrIndexActions.success): {
      const { widget, date, valid } = action.payload;
      const key = generateValidationKey(widget, date);
      return {
        ...state,
        validations: {
          ...state.validations,
          [key]: { loading: false, valid, error: null },
        },
      };
    }
    case getType(irAnalysisActions.validateIrIndexActions.failure): {
      const { widget, date, error } = action.payload;
      const key = generateValidationKey(widget, date);
      return {
        ...state,
        validations: {
          ...state.validations,
          [key]: { loading: false, valid: false, error },
        },
      };
    }
    case getType(irAnalysisActions.validateTradesActions.request): {
      const { widgetType } = action.payload;

      if (
        widgetType !== WidgetKeys.DEBT_HEDGE_SCHEDULE &&
        widgetType !== WidgetKeys.CASHFLOW_THROUGH_TIME
      ) {
        return state;
      }

      const widget = state[widgetType];

      return {
        ...state,
        [widgetType]: {
          ...widget,
          validations: {
            ...widget.validations,
            trades: {
              ...widget.validations.trades,
              loading: true,
              error: null,
            },
          },
        },
      };
    }

    case getType(irAnalysisActions.validateTradesActions.success): {
      const { widgetType, valid, tradeCompanyEntityId, debtCompanyEntityId } =
        action.payload;

      if (
        widgetType !== WidgetKeys.DEBT_HEDGE_SCHEDULE &&
        widgetType !== WidgetKeys.CASHFLOW_THROUGH_TIME
      ) {
        return state;
      }

      const widget = state[widgetType];

      return {
        ...state,
        [widgetType]: {
          ...widget,
          validations: {
            ...widget.validations,
            trades: {
              loading: false,
              valid,
              error: null,
              tradeCompanyEntityId,
              debtCompanyEntityId,
            },
          },
        },
      };
    }

    case getType(irAnalysisActions.validateTradesActions.failure): {
      const { widgetType, error } = action.payload;

      if (
        widgetType !== WidgetKeys.DEBT_HEDGE_SCHEDULE &&
        widgetType !== WidgetKeys.CASHFLOW_THROUGH_TIME
      ) {
        return state;
      }

      const widget = state[widgetType];

      return {
        ...state,
        [widgetType]: {
          ...widget,
          validations: {
            ...widget.validations,
            trades: {
              ...widget.validations.trades,
              loading: false,
              valid: false,
              error,
            },
          },
        },
      };
    }

    case getType(irAnalysisActions.validateDebtsActions.request): {
      const { widgetType } = action.payload;

      if (
        widgetType !== WidgetKeys.DEBT_HEDGE_SCHEDULE &&
        widgetType !== WidgetKeys.CASHFLOW_THROUGH_TIME
      ) {
        return state;
      }

      const widget = state[widgetType];

      return {
        ...state,
        [widgetType]: {
          ...widget,
          validations: {
            ...widget.validations,
            debts: {
              ...widget.validations.debts,
              loading: true,
              error: null,
            },
          },
        },
      };
    }

    case getType(irAnalysisActions.validateDebtsActions.success): {
      const { widgetType, valid, tradeCompanyEntityId, debtCompanyEntityId } =
        action.payload;

      if (
        widgetType !== WidgetKeys.DEBT_HEDGE_SCHEDULE &&
        widgetType !== WidgetKeys.CASHFLOW_THROUGH_TIME
      ) {
        return state;
      }

      const widget = state[widgetType];

      return {
        ...state,
        [widgetType]: {
          ...widget,
          validations: {
            ...widget.validations,
            debts: {
              loading: false,
              valid,
              error: null,
              tradeCompanyEntityId,
              debtCompanyEntityId,
            },
          },
        },
      };
    }

    case getType(irAnalysisActions.validateDebtsActions.failure): {
      const { widgetType, error } = action.payload;

      if (
        widgetType !== WidgetKeys.DEBT_HEDGE_SCHEDULE &&
        widgetType !== WidgetKeys.CASHFLOW_THROUGH_TIME
      ) {
        return state;
      }

      const widget = state[widgetType];

      return {
        ...state,
        [widgetType]: {
          ...widget,
          validations: {
            ...widget.validations,
            debts: {
              ...widget.validations.debts,
              loading: false,
              valid: false,
              error,
            },
          },
        },
      };
    }
    case getType(irAnalysisActions.saveUserWidgetDataActions.request): {
      return state;
    }
    case getType(irAnalysisActions.saveUserWidgetDataActions.success): {
      return state;
    }
    case getType(irAnalysisActions.saveUserWidgetDataActions.failure): {
      return state;
    }
    case getType(irAnalysisActions.fetchUserWidgetsActions.request):
      return {
        ...state,
        userWidgets: { data: [], loading: true, error: null },
      };

    case getType(irAnalysisActions.fetchUserWidgetsActions.success):
      return {
        ...state,
        userWidgets: {
          data: action.payload.widgets,
          loading: false,
          error: null,
        },
      };

    case getType(irAnalysisActions.fetchUserWidgetsActions.failure):
      return {
        ...state,
        userWidgets: {
          data: [],
          loading: false,
          error: action.payload.error,
        },
      };

    // disable debt and hedge if pricing summary simulated report toggle enabled
    case getType(irAnalysisActions.disableDebtAndHedgeAction):
      return { ...state, disableDebtAndHedge: action.payload };

    case getType(irAnalysisActions.disableCFTTAction):
      return { ...state, disableCFTT: action.payload };

    case getType(irAnalysisActions.getIrMarketEnvironmentDataForChart.request):
      return {
        ...state,
        swapRateChart: { ...state.swapRateChart, loading: true },
      };
    case getType(irAnalysisActions.getIrMarketEnvironmentDataForChart.success):
      return {
        ...state,
        swapRateChart: {
          ...state.swapRateChart,
          loading: false,
          data: Object.values(action.payload.historic_swap_rates.series),
        },
      };
    case getType(irAnalysisActions.getIrMarketEnvironmentDataForChart.failure):
      return {
        ...state,
        swapRateChart: {
          ...state.swapRateChart,
          loading: false,
        },
      };
    case getType(irAnalysisActions.resetIrMarketEnvironmentDataForChart):
      return {
        ...state,
        swapRateChart: { ...state.swapRateChart, data: [], loading: false },
      };

    default:
      return state;
  }
};

export default irAnalysisReducer;
